<template>
  <ModalComp
    :isVisible="isVisible"
    :title="modalTitle"
    size="xl"
    :hideFooter="true"
    @update:isVisible="$emit('update:isVisible', $event)"
  >
    <template #body>
      <div class="space-y-6">
        <div
          v-if="requiresUsCustomsCompliance"
          class="bg-red-50 border-2 border-red-500 rounded-lg p-5"
        >
          <div class="mb-4 flex items-center justify-between gap-3">
            <h5 class="font-bold text-red-900">
              <i class="fas fa-exclamation-triangle mr-2"></i>
              {{ usNoticeLanguage === "fr" ? "ATTENTION – EXPÉDITIONS VERS LES ÉTATS-UNIS" : "注意 – 发往美国的货件" }}
            </h5>
            <div class="flex shrink-0 rounded border border-red-400 bg-white p-1 text-xs">
              <button type="button" class="rounded px-2 py-1" :class="usNoticeLanguage === 'fr' ? 'bg-red-600 text-white' : 'text-red-800'" @click="usNoticeLanguage = 'fr'">Français</button>
              <button type="button" class="rounded px-2 py-1" :class="usNoticeLanguage === 'zh' ? 'bg-red-600 text-white' : 'text-red-800'" @click="usNoticeLanguage = 'zh'">中文</button>
            </div>
          </div>

          <div v-if="usNoticeLanguage === 'fr'" class="space-y-3 text-sm text-red-900">
            <p>Depuis le 8 juillet 2026, certaines marchandises expédiées vers les États-Unis doivent respecter de nouvelles obligations de conformité auprès de la CPSC, notamment via un GCC ou CPC selon le type de produit.</p>
            <p>Les vendeurs sont responsables des informations et documents transmis. En cas d’erreur, de document manquant ou de déclaration incorrecte, la marchandise peut être bloquée, retardée ou retournée par la douane américaine.</p>
            <p class="font-semibold">Les éventuels frais supplémentaires liés à une déclaration incorrecte ou incomplète seront à la charge du vendeur.</p>
            <p>Pour effectuer les démarches nécessaires : <a class="font-semibold underline" href="https://www.cpsc.gov/eFiling-CPSC-Product-Registry" target="_blank" rel="noopener noreferrer">www.cpsc.gov/eFiling-CPSC-Product-Registry</a></p>
            <p class="font-semibold">La facture commerciale doit notamment comporter :</p>
            <ul class="list-disc space-y-1 pl-5">
              <li>Numéro et date de facture</li><li>Numéro de commande</li><li>Coordonnées complètes de l’expéditeur et du destinataire</li><li>Références des articles : homme, femme ou enfant</li><li>Quantité et prix unitaire</li><li>Montant total et devise</li><li>Description des marchandises en anglais</li><li>Composition et type de fabrication : woven ou knitted</li><li>Pays d’origine et informations du fabricant</li><li>Code HS des produits</li>
            </ul>
            <p class="font-semibold">Merci de vérifier soigneusement ces informations avant toute expédition vers les États-Unis.</p>
          </div>

          <div v-else class="space-y-3 text-sm text-red-900">
            <p>自 2026 年 7 月 8 日起，部分发往美国的商品必须遵守美国消费品安全委员会（CPSC）的新合规要求。根据产品类型，可能需要提供 GCC（一般合格证书）或 CPC（儿童产品证书）。</p>
            <p>卖家须对所提交的信息和文件负责。如信息错误、文件缺失或申报不准确，货物可能被美国海关扣留、延误或退回。</p>
            <p class="font-semibold">因申报错误或不完整而产生的额外费用将由卖家承担。</p>
            <p>办理相关手续：<a class="font-semibold underline" href="https://www.cpsc.gov/eFiling-CPSC-Product-Registry" target="_blank" rel="noopener noreferrer">www.cpsc.gov/eFiling-CPSC-Product-Registry</a></p>
            <p class="font-semibold">商业发票至少应包含以下信息：</p>
            <ul class="list-disc space-y-1 pl-5">
              <li>发票编号和日期</li><li>订单编号</li><li>发件人和收件人的完整联系信息</li><li>商品类别及货号：男装、女装或童装</li><li>数量和单价</li><li>总金额和币种</li><li>英文商品描述</li><li>面料成分及制作类型：梭织（woven）或针织（knitted）</li><li>原产国及制造商信息</li><li>商品 HS 编码</li>
            </ul>
            <p class="font-semibold">发往美国前，请务必仔细核对上述信息。</p>
          </div>

          <button
            v-if="!usCustomsNoticeAccepted"
            type="button"
            class="mt-5 rounded-md bg-red-700 px-5 py-2 font-semibold text-white hover:bg-red-800 disabled:bg-gray-400"
            :disabled="isAcceptingUsNotice"
            @click="acceptUsNotice"
          >
            <i v-if="isAcceptingUsNotice" class="fas fa-spinner fa-spin mr-2"></i>
            {{ usNoticeLanguage === "fr" ? "J’ai lu et j’ai compris" : "我已阅读并理解" }}
          </button>
          <p v-else class="mt-4 font-semibold text-green-700">
            <i class="fas fa-check-circle mr-2"></i>{{ usNoticeLanguage === "fr" ? "Consignes enregistrées : cette commande passe en groupage eFashion pour le pickup." : "说明已确认：此订单将转为 eFashion 集运并安排取件。" }}
          </p>
        </div>

        <div
          v-if="showCustomsInvoiceReminder"
          class="bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <h5 class="mb-2 text-sm font-bold text-amber-950">
            <i class="fas fa-globe mr-2"></i>
            Destination soumise aux formalités douanières
          </h5>
          <p class="text-sm text-amber-900">
            Vérifiez que votre facture indique
            <strong>l’origine de la marchandise</strong> et une
            <strong>description des produits en anglais</strong>.
          </p>
        </div>

        <!-- Zone d'upload de facture, disponible après lecture de l'avertissement US -->
        <div
          v-if="shouldShowInvoiceUpload && canAccessUsShippingSteps"
          class="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
        >
          <h5 class="text-sm font-semibold text-red-600 mb-3">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            {{ requiresOutsideEuInvoice ? "Facture commerciale recommandée" : "Facture requise" }}
          </h5>
          <UploadZone
            :order-id="orderId"
            :order-group-id="orderGroupId"
            :order-date="orderDate"
            :invoice-id="invoiceId"
            @invoice-uploaded="handleInvoiceUploaded"
          />
        </div>

        <!-- Dimensions des colis : source unique pour le poids et les mesures. -->
        <ParcelDimensions
          v-if="shouldShowParcelDimensions"
          :order-id="orderId"
          :order-status="orderStatus"
          :nb-colis="nbColis || 1"
          :calculated-weight="orderData?.poids"
          :id-livraison="idLivraison"
          :country-code="countryCode ?? orderDetailData?.adresseLivraison?.pays?.code"
          :postal-code="postalCode ?? orderDetailData?.adresseLivraison?.codePostal"
          :title="
            !isGroupage
              ? 'Préparation de l’expédition'
              : undefined
          "
          @colis-count-changed="handleColisCountChanged"
          @update:valid="groupageDimensionsValid = $event"
        />
        <div
          v-if="shouldShowParcelDimensions"
          class="bg-amber-50 border border-amber-200 rounded-lg p-3"
        >
          <p class="text-sm text-amber-900">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            <strong>Attention :</strong> En cas de contrôle transporteur, si les dimensions ou le poids renseignés ne correspondent pas aux valeurs réelles et qu’une surfacturation nous est appliquée, celle-ci sera à votre charge.
          </p>
        </div>

        <!-- Retrait en magasin -->
        <div
          v-if="props.idLivraison === 2"
          class="bg-blue-50 border border-blue-200 rounded-lg p-4"
        >
          <p class="text-sm text-blue-800">
            <i class="fas fa-store mr-2"></i>
            <strong>Retrait en magasin</strong><br />
            Le client viendra retirer sa commande directement dans vos locaux.
            Aucune étiquette ni expédition n'est nécessaire.
          </p>
        </div>

        <!-- Livraison gratuite -->
        <div
          v-else-if="props.idLivraison === 3"
          class="bg-green-50 border border-green-200 rounded-lg p-4"
        >
          <p class="text-sm text-green-800">
            <i class="fas fa-gift mr-2"></i>
            <strong>Livraison offerte par eFashion</strong><br />
            Cette commande bénéficie d'une livraison gratuite offerte par
            eFashion Paris. Aucune action supplémentaire n'est requise de votre
            part.
          </p>
        </div>

        <!-- Transporteur acheteur (propre du client) -->
        <div
          v-else-if="isClientTransporter"
          class="bg-green-50 border border-green-200 rounded-lg p-4"
        >
          <p class="text-sm text-green-800">
            <i class="fas fa-truck mr-2"></i>
            <strong>Transporteur acheteur</strong><br />
            Cette commande utilise un transporteur sélectionné par l'acheteur.
            Les étiquettes doivent être générées manuellement ou par le
            transporteur lui-même.
          </p>
        </div>

        <!-- Groupage : dépassements bloquants du gabarit eFashion -->
        <div v-else-if="isGroupage" class="space-y-3">
          <div
            v-if="groupageDimensionErrors.length > 0"
            class="p-4 bg-red-50 border border-red-300 rounded-lg"
          >
            <p class="text-sm font-semibold text-red-900 mb-2">
              <i class="fas fa-exclamation-triangle mr-2"></i>
              Gabarit dépassé — répartissez le contenu dans deux colis ou plus :
            </p>
            <ul class="list-disc list-inside space-y-1">
              <li
                v-for="(err, i) in groupageDimensionErrors"
                :key="i"
                class="text-sm text-red-800"
              >
                {{ err }}
              </li>
            </ul>
          </div>
        </div>

        <!-- Composant dynamique selon le transporteur (commandes normales uniquement) -->
        <div v-else-if="labelModule && canAccessUsShippingSteps">
          <div
            v-if="hasUploadedInvoices && !parcelDimensionsValid"
            class="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          >
            <i class="fas fa-info-circle mr-1"></i>
            Renseignez les dimensions et le poids de tous les colis pour
            générer les étiquettes.
          </div>
        </div>

        <!-- Pas de transporteur défini -->
        <div v-else-if="canAccessUsShippingSteps" class="bg-red-50 border border-red-200 rounded-lg p-4">
          <p class="text-sm text-red-800">
            <i class="fas fa-exclamation-circle mr-2"></i>
            Aucun transporteur n'a été défini pour cette commande.
          </p>
        </div>
      </div>

      <!-- Boutons en bas de la modal (confirmation uniquement si commande encore nouvelle) -->
      <div
        v-if="isPendingConfirmationStatus"
        class="mt-6 flex justify-end gap-3"
      >
        <!-- Bouton Confirmer pour groupage -->
        <div
          v-if="isGroupage"
          class="flex flex-col gap-2 mx-auto mb-4"
        >
          <button
            @click="confirmGroupageOrder"
            class="px-6 py-3 text-base font-semibold rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            :class="
              groupageReadyToConfirm
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-400 text-black cursor-not-allowed'
            "
            type="button"
            :disabled="!groupageReadyToConfirm || isConfirming"
          >
            <i v-if="isConfirming" class="fas fa-spinner fa-spin mr-2"></i>
            <span v-if="isConfirming">
              {{ "Confirmation..." }}
            </span>
            <span v-else> Confirmer </span>
          </button>
        </div>

        <!-- Bouton Confirmer pour retrait en magasin -->
        <div v-else-if="props.idLivraison === 2" class="flex flex-col gap-2">
          <button
            @click="confirmPickupOrder"
            class="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            type="button"
            :disabled="isConfirming"
          >
            <i v-if="isConfirming" class="fas fa-spinner fa-spin mr-2"></i>
            {{ isConfirming ? "Confirmation..." : "Confirmer la commande" }}
          </button>
        </div>

        <!-- Bouton Confirmer pour livraison gratuite -->
        <div v-else-if="props.idLivraison === 3" class="flex flex-col gap-2">
          <button
            @click="confirmFreeShippingOrder"
            class="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            type="button"
            :disabled="isConfirming"
          >
            <i v-if="isConfirming" class="fas fa-spinner fa-spin mr-2"></i>
            {{ isConfirming ? "Confirmation..." : "Confirmer la commande" }}
          </button>
        </div>

        <!-- Bouton Confirmer pour transporteur acheteur -->
        <div v-else-if="isClientTransporter" class="flex flex-col gap-2">
          <button
            @click="confirmClientTransporterOrder"
            class="px-4 py-2 text-sm rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            :class="
              clientTransporterReadyToConfirm
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-400 text-black cursor-not-allowed'
            "
            type="button"
            :disabled="!clientTransporterReadyToConfirm || isConfirming"
            :title="
              !hasUploadedInvoices
                ? 'Veuillez d\'abord uploader une facture'
                : !groupageDimensionsValid
                  ? 'Veuillez renseigner des colis conformes au gabarit indiqué'
                : ''
            "
          >
            <i v-if="isConfirming" class="fas fa-spinner fa-spin mr-2"></i>
            {{ isConfirming ? "Confirmation..." : "Confirmer la commande" }}
          </button>

          <!-- Message d'erreur si pas de facture -->
          <div
            v-if="!hasUploadedInvoices"
            class="text-xs text-red-600 text-center"
          >
            <i class="fas fa-exclamation-triangle mr-1"></i>
            Veuillez d'abord uploader une facture
          </div>
          <div
            v-if="!groupageDimensionsValid"
            class="text-xs text-red-600 text-center"
          >
            <i class="fas fa-exclamation-triangle mr-1"></i>
            Renseignez tous les colis et respectez le gabarit indiqué
          </div>
        </div>
      </div>

      <!-- Génération d'étiquettes (commande déjà confirmée) -->
      <div
        v-else-if="canShowShippingLabelGenerateFooter"
        class="mt-6 flex flex-col items-stretch gap-2"
      >
        <button
          type="button"
          class="w-full px-6 py-3 text-base font-semibold rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          :class="
            canGenerateShippingLabels
              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
              : 'bg-gray-400 text-black cursor-not-allowed'
          "
          :disabled="!canGenerateShippingLabels || isGeneratingLabels"
          @click="generateShippingLabelsFromModal"
        >
          <i
            v-if="isGeneratingLabels"
            class="fas fa-spinner fa-spin mr-2"
          ></i>
          {{
            isGeneratingLabels
              ? "Génération en cours..."
              : "Générer vos étiquettes"
          }}
        </button>
        <p
          v-if="!hasUploadedInvoices"
          class="text-xs text-red-600 text-center"
        >
          <i class="fas fa-exclamation-triangle mr-1"></i>
          Veuillez d'abord uploader une facture
        </p>
        <p
          v-else-if="!parcelDimensionsValid"
          class="text-xs text-red-600 text-center"
        >
          <i class="fas fa-exclamation-triangle mr-1"></i>
          Renseignez les dimensions et le poids de tous les colis
        </p>
      </div>
    </template>
  </ModalComp>

</template>

<script setup lang="ts">
import { computed, inject, provide, ref, watch, type ComputedRef } from "vue";
import { ModalComp } from "@/components";
import ParcelDimensions from "@/components/order/ParcelDimensions.vue";
import UploadZone from "@/components/order/UploadZone.vue";
import {
  getLabelModule,
  isGpxCarrier as isGpxCarrierId,
} from "@/utils/labelModuleSelector";
import {
  getUploadedInvoices,
  getShippingDocument,
  saveParcelDimensions as persistParcelDimensions,
  acknowledgeUsCustomsNotice,
  updateStatus,
  OrderConfirmationCancelledError,
  generateLabelEasy,
} from "@/api/orders";
import { useNotification } from "@/composables/useNotification";
import { useCustomsDeclarationStore } from "@/stores/customsDeclaration";
import {
  getCarrierDimensionLimits,
  validateColisDimensions,
} from "@/utils/parcelDimensionsLimits";
import type { OrderDetailResponse } from "@/types";

interface Props {
  isVisible: boolean;
  orderId: number;
  orderStatus: number;
  idLivraison: number | null;
  countryCode?: string | null;
  postalCode?: string | null;
  carrierName?: string;
  orderDetailData?: OrderDetailResponse | null;
}

interface Emits {
  (e: "update:isVisible", value: boolean): void;
  (e: "labels-generated"): void;
  (e: "order-confirmed"): void;
  (e: "parcel-removed"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Récupérer les données de la commande depuis le parent (comme ComputedRef pour la réactivité)
// Si orderDetailData est passé en prop, l'utiliser, sinon essayer inject
const orderDetailDataFromInject = inject<
  ComputedRef<{ value: OrderDetailResponse | null }>
>(
  "orderDetailData",
  computed(() => ({ value: null })),
);
const orderDetailData = computed(() => {
  // Priorité à la prop si elle existe
  if (props.orderDetailData) {
    return { value: props.orderDetailData };
  }
  // Sinon utiliser inject
  return orderDetailDataFromInject.value;
});

// Helper pour accéder facilement aux données de la commande
const orderData = computed(() => orderDetailData.value?.value);

// Fournir orderDetailData aux composants enfants (Label components)
provide(
  "orderDetailData",
  computed(() => ({ value: orderData.value })),
);

// Déterminer si c'est un groupage
const isGroupage = computed(() => {
  if (!orderData.value) return false;
  const groupageValue = orderData.value.commandeGroupe?.groupage;
  const isSingleGpxOrder =
    isGpxCarrierId(props.idLivraison) &&
    Number(orderData.value.groupageReadiness?.countTotal) === 1;
  const explicitGroupage =
    groupageValue === 1 ||
    String(groupageValue) === "1" ||
    orderData.value.groupage === true;
  const singleGpxShipsDirect =
    isSingleGpxOrder && orderData.value.outsideEuShippingZone !== true;
  return explicitGroupage && !singleGpxShipsDirect;
});

// Nombre de colis pour ParcelDimensions (utilisé uniquement pour les groupages)
// Priorité : dynamicNbColis (mis à jour par ParcelDimensions) > nb_colis depuis la commande
const nbColis = computed(() => {
  return dynamicNbColis.value ?? orderData.value?.nb_colis ?? 1;
});

// Nombre dynamique de colis (mis à jour par ParcelDimensions)
const dynamicNbColis = ref<number | null>(null);
const hasParcelRemoval = ref(false);
const usNoticeLanguage = ref<"fr" | "zh">("fr");
const usCustomsNoticeAccepted = ref(false);
const isAcceptingUsNotice = ref(false);

// Dimensions groupage valides (rempli par ParcelDimensions via @update:valid)
const groupageDimensionsValid = ref(false);

// Déterminer si on doit afficher ParcelDimensions
// Conditions de l'ancien système :
// - Pour les commandes groupage et les transporteurs propres de l'acheteur
// - statut != 3 (pas annulé)
// - idLivraison != 3 (pas Free Shipping)
const shouldShowParcelDimensions = computed(() => {
  // Groupage en confirmation : dimensions demandées plus tard (avant téléchargement)
  if (isGroupage.value && [1, 9].includes(Number(props.orderStatus))) {
    return false;
  }

  if (
    !isGroupage.value &&
    !isClientTransporter.value &&
    !labelModule.value
  )
    return false;

  // Vérifier les autres conditions
  if (Number(props.orderStatus) === 3) return false; // Commande annulée
  if (props.idLivraison === 3) return false; // Free Shipping

  return true;
});

// Erreurs de dimensions — calculées depuis le store Pinia (accès direct au state pour la réactivité)
const customsStore = useCustomsDeclarationStore();
const parcelDimensions = computed(() =>
  customsStore.getParcelDimensions(props.orderId),
);
const parcelWeights = computed(() =>
  parcelDimensions.value.map((dimension) => Number(dimension.poids) || 0),
);
const groupageDimensionErrors = computed<string[]>(() => {
  const list =
    customsStore.declarations[props.orderId]?.parcelDimensions?.dimensions;
  if (!list || list.length === 0) return [];
  const lim = getCarrierDimensionLimits(props.idLivraison, props.countryCode ?? props.orderDetailData?.adresseLivraison?.pays?.code, props.postalCode ?? props.orderDetailData?.adresseLivraison?.codePostal);
  const errors: string[] = [];
  list.forEach((colis, idx) => {
    const num = idx + 1;
    const l = colis.longueur;
    const w = colis.largeur;
    const h = colis.hauteur;
    const p = colis.poids;
    if (
      l == null ||
      l <= 0 ||
      w == null ||
      w <= 0 ||
      h == null ||
      h <= 0 ||
      p == null ||
      p <= 0
    )
      return;
    if (p > lim.poids)
      errors.push(
        `Colis n°${num} : ${p} kg saisis, maximum ${lim.poids} kg.`,
      );
    const msg = validateColisDimensions(l, w, h, lim);
    if (msg) {
      errors.push(`Colis n°${num} : ${msg}.`);
    }
  });
  return errors;
});

const hasCompleteGroupageDimensions = computed(() => {
  const list =
    customsStore.declarations[props.orderId]?.parcelDimensions?.dimensions;
  if (!list || list.length === 0) return false;

  return list.every((colis) => {
    const l = colis.longueur;
    const w = colis.largeur;
    const h = colis.hauteur;
    const p = colis.poids;
    return (
      l != null &&
      l > 0 &&
      w != null &&
      w > 0 &&
      h != null &&
      h > 0 &&
      p != null &&
      p > 0
    );
  });
});

const preloadedGroupageValid = computed(() => {
  return (
    hasCompleteGroupageDimensions.value &&
    groupageDimensionErrors.value.length === 0
  );
});

// Les envois hors groupage lisent cette valeur directement du store afin que
// le bouton de génération se mette à jour dès la dernière dimension saisie.
const parcelDimensionsValid = computed(
  () => preloadedGroupageValid.value,
);

// Déterminer le module d'étiquette à utiliser
const labelModule = computed(() => {
  return getLabelModule(props.idLivraison, false);
});

const requiresUsCustomsCompliance = computed(
  () => orderData.value?.requiresUsCustomsCompliance === true,
);
const requiresOutsideEuInvoice = computed(
  () => orderData.value?.outsideEuShippingZone === true,
);
const showCustomsInvoiceReminder = computed(
  () =>
    !requiresOutsideEuInvoice.value &&
    orderData.value?.needsCustomsDeclaration === true &&
    !requiresUsCustomsCompliance.value,
);
const canAccessUsShippingSteps = computed(
  () => !requiresUsCustomsCompliance.value || usCustomsNoticeAccepted.value,
);

const groupageReadyToConfirm = computed(
  () => canAccessUsShippingSteps.value,
);

const acceptUsNotice = async () => {
  if (!props.orderId || isAcceptingUsNotice.value) return;
  isAcceptingUsNotice.value = true;
  try {
    usCustomsNoticeAccepted.value = await acknowledgeUsCustomsNotice(props.orderId);
    success("Commande passée en groupage eFashion");
    emit("parcel-removed");
  } catch (error) {
    showError(error instanceof Error ? error.message : "Impossible d’enregistrer votre confirmation");
  } finally {
    isAcceptingUsNotice.value = false;
  }
};


// Liste des transporteurs standards gérés par eFashion (inclut UPS groupage, Colissimo, DPD, etc.)
const STANDARD_TRANSPORTERS = [
  "1",
  "4",
  "8",
  "12",
  "16",
  "18",
  "2736",
  "2737",
  "2924",
  "2925", // Colissimo
  "7",
  "17", // OCX
  "10",
  "13", // DPD
  "19", // UPS (normal et groupage)
  "6",
  "11",
  "2649",
  "2915", // GPX
  "14",
  "15",
  "2742",
  "2931", // GLS
  "2729",
  "2730",
  "2731",
  "2732",
  "2747",
  "2917",
  "2918",
  "2919",
  "2920",
  "2921",
  "2922", // Chronopost
  "2733",
  "2734",
  "2735",
  "2738",
  "2739", // DHL
  "2748",
  "2926",
  "2927",
  "2928",
  "2929",
  "2930", // Fedex
  "2743",
  "2744",
  "2745",
  "2923", // UPS Express Saver (Easy)
  "2932", // UPS Standard (Easy)
  "2746", // Easy Colissimo
  "2741", // OCX Africa
  "2916", // Bolloré
];

// Pour les groupages : ne jamais appeler l'API Easy ici (étiquettes générées côté eFashion ou par le transporteur).
const shouldGenerateLabelsOnConfirm = computed(() => {
  if (isGroupage.value) return false;
  return false;
});

// Détecter si c'est un transporteur propre du client (acheteur)
const isClientTransporter = computed(() => {
  const livraison = orderData.value?.livraison;
  if (livraison && "id_acheteur" in livraison) {
    return livraison.id_acheteur != null;
  }

  return (
    props.idLivraison &&
    !STANDARD_TRANSPORTERS.includes(String(props.idLivraison))
  );
});

const clientTransporterReadyToConfirm = computed(
  () =>
    canAccessUsShippingSteps.value &&
    hasUploadedInvoices.value &&
    groupageDimensionsValid.value,
);

// Titre du modal selon le transporteur
const modalTitle = computed(() => {
  if (props.carrierName) {
    return `Génération d'étiquettes - ${props.carrierName}`;
  }
  return "Génération d'étiquettes de colis";
});

// Gérer la génération réussie des étiquettes
const handleLabelsGenerated = () => {
  emit("labels-generated");
  emit("update:isVisible", false);
};

// Vérifier les factures pour les groupages
const hasUploadedInvoices = ref(false);
const orderGroupId = computed(() => {
  return orderData.value?.id_commande_groupe || 0;
});
const orderDate = computed(() => {
  return (
    orderData.value?.commandeGroupe?.date_creation || new Date().toISOString()
  );
});
const invoiceId = computed(() => {
  return orderData.value?.id_commande_name || undefined;
});

// Notifications
const { success, error: showError } = useNotification();

const canGenerateShippingLabels = computed(
  () =>
    parcelDimensionsValid.value &&
    hasUploadedInvoices.value &&
    ![1, 3, 9].includes(Number(props.orderStatus)),
);

const isPendingConfirmationStatus = computed(() =>
  [1, 9].includes(Number(props.orderStatus)),
);

const canShowShippingLabelGenerateFooter = computed(() => {
  if (isPendingConfirmationStatus.value) return false;
  if (isGroupage.value || isClientTransporter.value) return false;
  if (props.idLivraison === 2 || props.idLivraison === 3) return false;
  if (!labelModule.value || !canAccessUsShippingSteps.value) return false;
  return ["colissimo", "dpd", "ups", "easy"].includes(labelModule.value);
});

const isGeneratingLabels = ref(false);

const generateShippingLabelsFromModal = async () => {
  if (!canGenerateShippingLabels.value || isGeneratingLabels.value) return;
  if (!props.orderId || !labelModule.value) return;

  try {
    isGeneratingLabels.value = true;
    await customsStore.saveDimensionsToDB(props.orderId);

    const dimensions = customsStore.getParcelDimensions(props.orderId);
    const colisPoids = dimensions.map((dim) => Number(dim.poids) || 0);
    const response = await generateLabelEasy({
      id_commande: props.orderId,
      nbColis: dimensions.length || nbColis.value,
      colisPoids,
      parcelDimensions: dimensions,
    });

    if (response.success) {
      success("Étiquettes générées avec succès");
      handleLabelsGenerated();
    } else {
      showError(
        response.error || "Erreur lors de la génération des étiquettes",
      );
    }
  } catch (error) {
    showError(
      error instanceof Error
        ? error.message
        : "Erreur lors de la génération des étiquettes",
    );
  } finally {
    isGeneratingLabels.value = false;
  }
};

// Vérifier si on doit afficher la zone d'upload de facture
const shouldShowInvoiceUpload = computed(() => {
  // Hors UE : la facture est obligatoire dès la validation, même si la
  // commande vient d'être basculée en groupage.
  if (
    isGroupage.value &&
    [1, 9].includes(Number(props.orderStatus)) &&
    !requiresOutsideEuInvoice.value
  ) {
    return false;
  }
  // Afficher uniquement si aucune facture n'a été trouvée
  return !hasUploadedInvoices.value;
});

// Vérifier les factures au montage
const checkInvoices = async () => {
  if (!props.orderId) return;
  const [invoicesResult, shippingDocumentResult] = await Promise.allSettled([
    getUploadedInvoices(props.orderId),
    getShippingDocument(props.orderId),
  ]);
  hasUploadedInvoices.value =
    (invoicesResult.status === "fulfilled" &&
      invoicesResult.value.length > 0) ||
    (shippingDocumentResult.status === "fulfilled" &&
      shippingDocumentResult.value.exists);
};

const preloadParcelDimensions = () => {
  const raw = orderData.value?.data;
  if (!raw) return;

  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
  }

  const dimensions = (
    parsed as {
      infocolis?: {
        dimensioncolis?: Array<{
          longueur?: unknown;
          largeur?: unknown;
          hauteur?: unknown;
          poids?: unknown;
        }>;
      };
    }
  )?.infocolis?.dimensioncolis;

  if (!Array.isArray(dimensions) || dimensions.length === 0) return;

  const toNumberOrNull = (value: unknown): number | null => {
    const number = Number(value);
    return value !== "" && value != null && Number.isFinite(number)
      ? number
      : null;
  };

  customsStore.saveParcelDimensions(
    props.orderId,
    dimensions.map((dimension) => ({
      longueur: toNumberOrNull(dimension.longueur),
      largeur: toNumberOrNull(dimension.largeur),
      hauteur: toNumberOrNull(dimension.hauteur),
      poids: toNumberOrNull(dimension.poids),
    })),
  );
};

// Gérer l'upload d'une facture
const handleInvoiceUploaded = async () => {
  // Recharger les factures
  await checkInvoices();
  // Émettre un événement pour les composants enfants
  window.dispatchEvent(
    new CustomEvent("invoiceUploaded", { detail: { orderId: props.orderId } }),
  );
};

// Gérer le changement du nombre de colis depuis ParcelDimensions
const handleColisCountChanged = (newCount: number, removedIndex?: number) => {
  dynamicNbColis.value = newCount;
  if (removedIndex != null) hasParcelRemoval.value = true;
};

watch(
  () => props.isVisible,
  (visible) => {
    if (!visible && hasParcelRemoval.value) {
      hasParcelRemoval.value = false;
      emit("parcel-removed");
    }
  },
);

const isConfirming = ref(false);

// Confirmer la commande groupage (statut 7 ou 8 selon statut actuel)
const confirmGroupageOrder = async () => {
  if (!props.orderId) return;
  if (!groupageReadyToConfirm.value) {
    showError(
      "Veuillez d'abord lire et accepter les consignes d'expédition.",
    );
    return;
  }
  await performGroupageConfirmation();
};

const performGroupageConfirmation = async () => {
  if (!props.orderId) return;
  try {
    isConfirming.value = true;

    const newStatus = props.orderStatus === 9 ? 8 : 7;
    await updateStatus(props.orderId, newStatus);
    emit("order-confirmed");
    emit("update:isVisible", false);
    success("Commande groupage confirmée avec succès !");
    setTimeout(() => {
      window.location.reload();
    }, 500);
  } catch (error) {
    if (error instanceof OrderConfirmationCancelledError) return;
    showError(
      error instanceof Error
        ? error.message
        : "Erreur lors de la confirmation du groupage",
    );
  } finally {
    isConfirming.value = false;
  }
};

const confirmClientTransporterOrder = async () => {
  if (!props.orderId) return;
  if (!clientTransporterReadyToConfirm.value) {
    showError(
      "La facture est obligatoire et tous les colis doivent respecter le gabarit indiqué.",
    );
    return;
  }
  if (groupageDimensionErrors.value.length > 0) {
    showError(
      "Un colis dépasse le gabarit autorisé. Répartissez son contenu dans deux colis ou plus.",
    );
    return;
  }
  await performClientTransporterConfirmation();
};

const performClientTransporterConfirmation = async () => {
  if (!props.orderId) return;
  try {
    isConfirming.value = true;
    const dimensions = customsStore.getParcelDimensions(props.orderId);
    await persistParcelDimensions(props.orderId, dimensions);

    // Pour les transporteurs acheteur, utiliser le même statut que OCX (7 = attente d'enlèvement)
    await updateStatus(props.orderId, 7);

    emit("order-confirmed");
    emit("update:isVisible", false);

    success("Commande confirmée avec succès !");

    setTimeout(() => {
      window.location.reload();
    }, 500);
  } catch (error) {
    if (error instanceof OrderConfirmationCancelledError) return;
    showError(
      error instanceof Error
        ? error.message
        : "Erreur lors de la confirmation de la commande",
    );
  } finally {
    isConfirming.value = false;
  }
};

const confirmPickupOrder = async () => {
  if (!props.orderId) return;

  try {
    isConfirming.value = true;
    // Pour les retraits en magasin, passer directement au statut 4 (confirmée)
    await updateStatus(props.orderId, 4);

    emit("order-confirmed");
    emit("update:isVisible", false);

    success("Retrait en magasin confirmé avec succès !");

    setTimeout(() => {
      window.location.reload();
    }, 500);
  } catch (error) {
    // Erreur silencieuse - la confirmation a échoué
  } finally {
    isConfirming.value = false;
  }
};

const confirmFreeShippingOrder = async () => {
  if (!props.orderId) return;

  try {
    isConfirming.value = true;
    // Pour les livraisons gratuites, passer directement au statut 4 (confirmée)
    await updateStatus(props.orderId, 4);

    emit("order-confirmed");
    emit("update:isVisible", false);

    success("Livraison offerte confirmée avec succès !");

    setTimeout(() => {
      window.location.reload();
    }, 500);
  } catch (error) {
    // Erreur silencieuse - la confirmation a échoué
  } finally {
    isConfirming.value = false;
  }
};

// Vérifier les factures quand la modal devient visible ou quand orderData change
watch(
  [() => props.isVisible, () => orderData.value],
  async ([isVisible, data]) => {
    if (isVisible && data) {
      usCustomsNoticeAccepted.value =
        data.usCustomsNoticeAccepted === true;
      // Toujours vérifier les factures pour toutes les commandes
      await checkInvoices();
      preloadParcelDimensions();
      if (shouldShowParcelDimensions.value) {
        groupageDimensionsValid.value = preloadedGroupageValid.value;
      }
    }
  },
  { immediate: true },
);
</script>
